import {
  FPError,
  Search,
  HotelSearchCriteria,
  Currency,
  Filter,
  Lang,
  Suggest,
  HotelDetailsML,
  HotelOffers,
  Deferred,
} from 'types';
import { GetterTree, MutationTree, ActionTree, ActionContext } from 'vuex';
import { differenceInDays, parse } from 'date-fns';
import { HotelService, NetworkHelper } from 'api';
import { Subscription } from 'rxjs';
import { Expedia } from '@/3ds/expedia';
import { finalize } from 'rxjs/operators';
import { Events } from '@/analytics/events';
import { Log, mapError } from '@/utils';
import store from '..';
import i18n from '@/plugins/i18n';
import cloneDeep from 'lodash/cloneDeep';
import { ExpirationCountdown } from '@/plugins/expiration-countdown';

import {
  hideTrimmed,
  mapHotelOffers,
  mapOffersFilter,
  mapPublicPrices,
} from '@/mapper/hotelOffers';

interface SubscriptionWithArgs {
  subscription: Subscription;
  criteria: string;
}

let offersSubscription: SubscriptionWithArgs = null;
let hotelSubscription: Subscription = null;

export class State {
  request_id: string = '';
  hotel: HotelDetailsML.Details = null;
  groups: HotelOffers.Group[] = [];
  selectedOffer: HotelOffers.Offer = null;
  amenities: Search.HotelAmenityModel[] = [];
  loading: boolean = false;
  offersLoading: boolean = false;
  error: FPError = null;
  searchDestinationTitle: string = '';

  filters: HotelOffers.FilterSectionModel[] = [];
  reviews: HotelOffers.Review[] = [];
  searchProps: HotelOffers.SearchProps = null;
  searchCurrency: string = '';
  media: HotelOffers.Media = null;
  mediaLoading: boolean = false;
  isOffersTrimmed: boolean = HotelOffers.OffersTrim.isTrimming();

  // offers expiration
  offersExpirationAt: Date = null;
  isOffersExpired: boolean = false;

  // offer policy expiration
  policyExpirationAt: Date = null;
  isPolicyExpired: boolean = false;

  localizedPolicy: {
    isLoading: boolean;
    policy: HotelDetailsML.Policy | undefined;
    descriptions: HotelDetailsML.Descriptions | undefined;
  } = {
    isLoading: false,
    policy: undefined,
    descriptions: undefined,
  };

  carousel: {
    id: string;
    isOpen: boolean;
    images: string[];
  } = {
    id: null,
    isOpen: false,
    images: [],
  };
}

const getters = <GetterTree<State, any>>{
  hotelName(state: State): string {
    return state.hotel ? state.hotel.name : '';
  },

  address(state: State): string {
    if (state.hotel) {
      const location = state.hotel.location;

      if (location.country?.toLocaleUpperCase() === 'US') {
        const statePlusZip = `${location.state ? location.state : ''}${
          location.state && location.zip ? ' ' : ''
        }${location.zip ? location.zip : ''}`;

        return [
          location.address,
          location.city,
          statePlusZip,
          location.countryName,
        ]
          .filter(it => it)
          .reduce((acc, s) => acc + ', ' + s);
      } else {
        return [location.address, location.city, location.countryName]
          .filter(it => it)
          .reduce((acc, s) => acc + ', ' + s);
      }
    } else return '';
  },
  hotelLocation(state: State): HotelOffers.Location {
    return state.hotel ? state.hotel.location : null;
  },
  ratingString(state: State): string {
    return state.hotel && state.hotel.rating.reviewsCount > 0
      ? `${state.hotel.rating.reviewScore}/${state.hotel.rating.reviewScoreMax} ${state.hotel.rating.text}`
      : '';
  },

  reviewsCount(state: State): number {
    return state.hotel ? state.hotel.rating.reviewsCount : 0;
  },

  rating(state: State): number {
    return state.hotel ? state.hotel.rating.reviewScore : 0;
  },

  rooms(state: State): HotelOffers.Group[] {
    const groupsList =
      state.filters && state.filters.every(it => it.options.nothingSelected())
        ? state.groups
        : state.groups
            .slice()
            .map(group => {
              const tmp = { ...group };

              tmp.offers = group.offers.filter(offer =>
                state.filters.every(
                  section =>
                    section.options.nothingSelected() ||
                    (HotelOffers.OPTION_FILTERS_HEADERS.some(it =>
                      section.header.toLowerCase().includes(it.toLowerCase()),
                    )
                      ? section.options
                          .filterSelected()
                          .every(it =>
                            offer.filterRef.includes(+(it as Filter.Option).id),
                          )
                      : section.options
                          .filterSelected()
                          .every(it =>
                            offer.filterRef.includes(+(it as Filter.Option).id),
                          )),
                ),
              );
              return tmp;
            })
            .filter(it => it.offers.length > 0);

    const supportedSuppliers = (): HotelOffers.PublicPriceSource[] => {
      const ids: Record<string, HotelOffers.PublicPriceSource[]> = {
        B_: [HotelOffers.PublicPriceSource.BOOKING],
        E_: [HotelOffers.PublicPriceSource.EXPEDIA],
        BE_: [
          HotelOffers.PublicPriceSource.BOOKING,
          HotelOffers.PublicPriceSource.EXPEDIA,
        ],
      };

      const urlPath = window.location.pathname;

      return Array.from(
        new Set(
          Object.keys(ids).flatMap(it => {
            return urlPath.includes(it) ? ids[it] : [];
          }),
        ),
      );
    };

    const hideUnsupportedSuppliers = (
      group: HotelOffers.Group,
    ): HotelOffers.Group => {
      const clone = cloneDeep(group) as HotelOffers.Group;
      clone.publicPrice = clone.publicPrice.filter(it =>
        supportedSuppliers().includes(it.name),
      );
      return clone;
    };

    const recalculatePublicPrices = (
      group: HotelOffers.Group,
    ): HotelOffers.Group => {
      if (
        state.filters &&
        state.filters.every(it => it.options.nothingSelected())
      ) {
        return group;
      }

      const clone = cloneDeep(group) as HotelOffers.Group;
      const recalculatedPublics = mapPublicPrices(clone);

      clone.publicPrice = recalculatedPublics;

      return clone;
    };

    const sortOffers = (group: HotelOffers.Group): HotelOffers.Group => {
      const clone = cloneDeep(group);
      clone.offers = group.offers.slice().sortBy(it => it.price.effectivePrice);
      return clone;
    };

    return groupsList.length > 0
      ? groupsList
          .slice()
          .map(it => hideUnsupportedSuppliers(it))
          .map(it => recalculatePublicPrices(it))
          .map(it => hideTrimmed(it, !state.isOffersTrimmed))
          .map(it => sortOffers(it))
          .filter(it => it.offers.length > 0)
          .sortBy(
            it =>
              it.offers.min(it => it.price.effectivePrice).price.effectivePrice,
          )
      : [];
  },

  hotelImages(state: State): string[] {
    return state.hotel ? state.hotel.photos.map(it => it.url.standard) : [];
  },

  hotelMediaThbBgcolor(state: State): string {
    return state.media
      ? state.media['thb-bgcolor']
        ? state.media['thb-bgcolor']
        : state.media.bgcolor
        ? state.media.bgcolor
        : null
      : null;
  },

  stars(state: State): number {
    return state.hotel ? state.hotel.rating.stars : 0;
  },

  getHotelId(state: State) {
    return state.hotel ? state.hotel.id : '';
  },

  getCheckin(state: State, getters, rootState) {
    const from = state.hotel?.checkinCheckoutTimes.checkinFrom;
    const to = state.hotel?.checkinCheckoutTimes.checkinTo;

    if (from && to && from !== to) {
      return String(
        i18n.global.t('hotelpage.policies.checkInFromTo', { from, to }),
      );
    } else if (from) {
      return String(i18n.global.t('hotelpage.policies.checkIn', { from }));
    } else {
      return '';
    }
  },

  getCheckout(state: State, getters, rootState) {
    const from = state.hotel?.checkinCheckoutTimes.checkoutFrom;
    const to = state.hotel?.checkinCheckoutTimes.checkoutTo;

    if (from && to && from !== to) {
      return String(
        i18n.global.t('hotelpage.policies.checkOutFromTo', { from, to }),
      );
    } else if (to) {
      return String(
        i18n.global.t('hotelpage.policies.checkOut', { before: to }),
      );
    } else {
      return '';
    }
  },

  hotelPolicies(state: State): HotelDetailsML.Policy {
    return state.hotel && state.hotel.policy ? state.hotel.policy : null;
  },

  isHasDescriptions(state: State): boolean {
    if (!state.hotel?.descriptions) return false;

    if (
      Object.keys(state.hotel.descriptions).every(
        key =>
          !state.hotel?.descriptions[key as keyof HotelDetailsML.Descriptions],
      )
    ) {
      return false;
    }

    return true;
  },

  isHasAmenities(state: State): boolean {
    return (
      state.amenities &&
      state.amenities.length > 0 &&
      state.amenities.filter(it => it.active).length > 0
    );
  },

  isHasReviews(state: State): boolean {
    return state.reviews.length > 0;
  },

  isOffersSoldOut(state: State) {
    // if (state.groups.length === 0) return true;
    // const allOffers = ([] as HotelOffers.Offer[]).concat(...state.groups.map((it) => it.offers));
    // const soldoutOffers = allOffers.filter((it) => it.soldout);
    // if (soldoutOffers.length >= 2) return true;
    // if (allOffers.length === soldoutOffers.length) return true;
    return false;
  },
  needRefresh(state: State) {
    return state.error ? true : false;
  },
  errorHeader(state: State) {
    if (!state.error) return '';
    switch (state.error.header) {
      case 'OFFER_EXPIRED':
        return i18n.global.t('errors.hotel.OFFER_EXPIRED');
      case 'NO_ROOMS':
        return i18n.global.t('errors.hotel.NO_ROOMS');
      case 'NETWORK_ERROR':
        return i18n.global.t('errors.hotel.NETWORK_ERROR');
      default:
        return i18n.global.t('errors.hotel.DEFAULT_HEADER');
    }
  },
};

const mutations = <MutationTree<State>>{
  setSearchProps(state: State, props: HotelOffers.SearchProps) {
    state.searchProps = props;
  },

  setHotel(state: State, hotel: HotelDetailsML.Details) {
    state.hotel = hotel;
  },

  setOffers(state: State, groups: HotelOffers.Group[]) {
    state.groups = groups;
  },

  setSelectedOffer(state: State, offer: HotelOffers.Offer) {
    state.selectedOffer = offer ? offer : null;
  },

  setRequestId(state: State, id: string) {
    state.request_id = id;
  },

  setFilters(state: State, filters: HotelOffers.FilterSectionModel[]) {
    state.filters = filters;
  },

  setAmenities(state: State, amenities: Search.HotelAmenityModel[]) {
    state.amenities = amenities;
  },

  setReviews(state: State, reviews: HotelOffers.Review[]) {
    state.reviews = reviews;
  },

  setSearchCurrency(state: State, currency: string) {
    state.searchCurrency = currency;
  },

  clearFilters(state: State) {
    state.filters.forEach(section => section.clear());
  },

  toggleRoomFilter(state: State, id: number[]) {
    const option = state.filters[id[0]].options[id[1]] as Filter.Option;
    option.toggle();
    let tween: Filter.Option;
    if (option.id === HotelOffers.FilterKeys.PayNow) {
      tween = state.filters[id[0]].options.find(
        v => v.id === HotelOffers.FilterKeys.PayLater,
      );
      tween.toggle(false);
    }
    if (option.id === HotelOffers.FilterKeys.PayLater) {
      tween = state.filters[id[0]].options.find(
        v => v.id === HotelOffers.FilterKeys.PayNow,
      );
      tween.toggle(false);
    }
  },

  setLoading(state: State, loading: boolean) {
    state.loading = loading;
  },

  setOffersLoading(state: State, loading: boolean) {
    state.offersLoading = loading;
  },

  setError(state: State, error: FPError) {
    state.error = error;
  },

  resetStore(state: State) {
    state.error = null;
    state.loading = true;
    state.groups = [];
    state.hotel = null;
    state.selectedOffer = null;
    state.amenities = [];
    state.offersExpirationAt = null;
    state.isOffersExpired = false;
    state.policyExpirationAt = null;
    state.isPolicyExpired = false;
    state.localizedPolicy = {
      isLoading: false,
      policy: undefined,
      descriptions: undefined,
    };
    state.carousel = {
      id: null,
      isOpen: false,
      images: [],
    };

    // reset expirations
    ExpirationCountdown.stop();
  },

  setSearchDestinationTitle(state: State, title: string) {
    state.searchDestinationTitle = title;
  },

  setOfferLoading(
    state: State,
    { offerId, loading }: { offerId: string; loading: boolean },
  ) {
    const selectedGroupIndex = state.groups.findIndex(
      group => !!group.offers.find(it => it.id === offerId),
    );

    const selectedOfferIndex =
      state.groups && selectedGroupIndex > -1
        ? state.groups[selectedGroupIndex].offers.findIndex(
            it => it.id === offerId,
          )
        : null;

    if (state.groups && selectedGroupIndex > -1 && selectedOfferIndex > -1) {
      state.groups[selectedGroupIndex].offers[selectedOfferIndex].loading =
        loading;
    }
  },

  setOfferSoldout(
    state: State,
    { priceToken, soldout }: { priceToken: string; soldout: boolean },
  ) {
    const selectedGroupIndex = state.groups.findIndex(
      group => !!group.offers.find(it => it.price._token === priceToken),
    );
    const selectedOfferIndex =
      state.groups && selectedGroupIndex > -1
        ? state.groups[selectedGroupIndex].offers.findIndex(
            it => it.price._token === priceToken,
          )
        : null;
    if (state.groups && selectedGroupIndex > -1 && selectedOfferIndex > -1) {
      state.groups[selectedGroupIndex].offers[selectedOfferIndex].soldout =
        soldout;
      store.dispatch(
        'support/setRefreshDialogShow',
        { show: true, message: null },
        { root: true },
      );
      state.groups[selectedGroupIndex].offers[selectedOfferIndex].soldout =
        soldout;
      store.dispatch(
        'support/setRefreshDialogShow',
        { show: true, message: null },
        { root: true },
      );
    }
  },

  setHotelMedia(state: State, media: HotelOffers.Media) {
    state.media = media;
  },

  setHotelMediaLoading(state: State, isLoading: boolean) {
    state.mediaLoading = isLoading;
  },

  updateHotelMediaValue(
    state: State,
    { index, value }: { index: number; value: string },
  ) {
    if (state.media && state.media.images) {
      state.media.images[index] = value;
      state.carousel.images[index] = value;
    }
  },

  updateImage(
    state: State,
    { index, value }: { index: number; value: string },
  ) {
    const { id } = state.carousel;

    if (!id) {
      return;
    }

    if (
      id === 'offer' &&
      state.selectedOffer &&
      state.selectedOffer.images[index]
    ) {
      state.selectedOffer.images[index] = value;
      state.carousel.images[index] = value;
      return;
    }

    const group = state.groups.find(it => it.name === id);

    if (group && group.images[index]) {
      group.images[index] = value;
      state.carousel.images[index] = value;
    }
  },

  setIsOffersTrimmed(state: State, isTrimmed: State['isOffersTrimmed']) {
    state.isOffersTrimmed = isTrimmed;
  },

  // offers expiration
  setOffersExpiration(state: State, expirationAt: Date) {
    state.offersExpirationAt = expirationAt;
  },
  setOffersExpired(state: State, isExpired: boolean) {
    state.isOffersExpired = isExpired;
  },

  // offers expiration
  setPolicyExpirationTime(state: State, expirationAt: Date) {
    state.policyExpirationAt = expirationAt;
  },
  setPolicyExpired(state: State, isExpired: boolean) {
    state.isPolicyExpired = isExpired;
  },

  // clear policy and offers expiration
  clearExpirations(state: State) {
    state.offersExpirationAt = null;
    state.isOffersExpired = false;

    state.policyExpirationAt = null;
    state.isPolicyExpired = false;
  },

  setLocalizedPolicyLoading(state: State, isLoading: boolean) {
    state.localizedPolicy.isLoading = isLoading;
  },

  setLocalizedPolicy(state: State, policy: State['localizedPolicy']) {
    state.localizedPolicy = policy;
  },

  toggleCarousel(
    state: State,
    payload: { isOpen: boolean; images?: string[]; id?: string },
  ) {
    if (payload.isOpen && payload.id && payload.images?.length) {
      state.carousel = {
        id: payload.id,
        images: payload.images,
        isOpen: true,
      };
      return;
    }

    state.carousel = {
      id: null,
      images: [],
      isOpen: false,
    };
  },
};

const actions = <ActionTree<State, any>>{
  getHotel(store: ActionContext<State, any>) {
    store.commit('setLoading', true);
    store.dispatch('support/refreshOfferId', {}, { root: true });
    store.commit('resetStore');
    store.dispatch('getHotelInfo');
  },

  async getHotelInfo(store: ActionContext<State, any>) {
    const criteria = store.rootState.searchCriteria.criteria;

    if (hotelSubscription) {
      hotelSubscription.unsubscribe();
    }

    hotelSubscription = HotelService.searchV2GetHotel(criteria.id)
      .pipe(finalize(() => store.commit('setLoading', false)))
      .subscribe(
        hotel => {
          store.commit('setHotel', hotel);

          store.dispatch('support/updateIntercomSettings', {}, { root: true });

          const media = {
            thumbnail: hotel.thumb,
            bgcolor: hotel.bgColor,
            images: hotel.photos.map(it => it.url.standard),
          };

          store.commit('setHotelMedia', media);

          store.commit(
            'setAmenities',
            Search.HotelAmenitiesFactory.composeDetails(
              hotel.featuredAmenities,
            ),
          );

          if (store.rootState.user.userV2) {
            store.dispatch('getOffers');
          }
        },
        _ => {
          store.commit('setError', {
            header: 'NO_HOTEL',
            message: i18n.global.t('errors.hotel.NO_HOTEL'),
          });
        },
      );
  },

  async getOffers(
    store: ActionContext<State, any>,
    withReload?: boolean,
  ): Promise<HotelOffers.Offer[]> {
    const offers = new Deferred<HotelOffers.Offer[]>();

    if (!store.rootState.user.userV2) {
      return [];
    }

    const criteriaURI =
      window.location.pathname +
      window.location.search +
      NetworkHelper.getLocale() +
      NetworkHelper.getCurrency().currency;

    if (offersSubscription) {
      if (offersSubscription.criteria === criteriaURI) {
        return [];
      } else {
        offersSubscription.subscription.unsubscribe();
        offersSubscription = null;
      }
    }

    store.commit('setOffersLoading', true);
    store.dispatch('clearExpirationCountdown', null);

    store.dispatch(
      'support/setRefreshDialogShow',
      { show: false, message: null },
      { root: true },
    );

    if (withReload) {
      store.commit('setOffers', []);
      store.commit('setFilters', []);
      store.commit('setRequestId', '');
      store.commit('setError', null);
    }
    const criteria: HotelSearchCriteria =
      store.rootState.searchCriteria.criteria;

    const suggest = (await store.rootGetters['suggest/getSuggest'](
      Suggest.Ids.HOTEL,
    )) as Suggest.Suggest<Suggest.HotelSuggest>;

    const CURRENCY = store.rootState.support.currency as Currency.CurrencyItem;

    store.commit('setOffersLoading', true);
    store.commit('setFilters', []);

    if (differenceInDays(parse(criteria.checkin), new Date()) < 0) {
      await store.dispatch(
        'searchCriteria/initDefaultSearchCriteria',
        criteria.id,
        {
          root: true,
        },
      );
    }

    const startTime = new Date().getTime();

    Events.Hotel.Offer.offersStart(criteria, suggest);

    const subscription = HotelService.getHotelv2(criteria, CURRENCY.currency)
      .pipe(finalize(() => store.commit('setOffersLoading', false)))
      .pipe(
        finalize(() => {
          Events.Hotel.Offer.offersFinish({
            criteria,
            startTime,
            requestId: store.state.request_id,
            hotel: store.state.hotel,
            offers: store.state.groups,
            error: store.state.error,
          });
        }),
      )
      .pipe(finalize(() => (offersSubscription = null)))
      .subscribe(
        res => {
          if (!res) {
            throw new Error();
          }

          const { groups, filter, expirationTime } = res.data;

          if (res.data.locale) {
            store.commit('setSearchCurrency', CURRENCY.currency);
          }

          // store.commit('setReviews', res.hotel.reviews ? res.hotel.reviews : []);

          if (res.data.groups && res.data.groups.length > 0) {
            const offers = mapHotelOffers(groups, filter, criteria.adults);
            store.commit('setOffers', offers);

            const mappedFilters =
              offers.length > 0 ? mapOffersFilter(offers, filter) : [];
            store.commit('setFilters', mappedFilters);

            const expirationDatetime = new Date(expirationTime);
            const expirationAt = expirationDatetime.isValid()
              ? expirationDatetime
              : null;
            store.commit('setOffersExpiration', expirationAt);
            ExpirationCountdown.init(expirationAt, () =>
              store.commit('setOffersExpired', true),
            );
          } else {
            store.commit('setError', {
              header: 'NO_ROOMS',
              message: i18n.global.t('errors.hotel.NO_ROOMS_MESSAGE'),
            });
            store.commit('setOffers', []);
          }

          store.commit('setRequestId', res.datetime ? res.datetime : '');

          offers.resolve(groups.flatMap(group => group.offers));
        },
        err => {
          const error = mapError(err);
          store.commit('setError', error);

          offers.resolve([]);
        },
      );

    offersSubscription = {
      subscription,
      criteria: criteriaURI,
    };

    return await offers.promise;
  },

  async selectOffer(
    store: ActionContext<State, any>,
    { offer }: { offer: HotelOffers.Offer },
  ) {
    if (!offer) {
      store.commit('setSelectedOffer', null);
      store.commit('setPolicyExpirationTime', null);
      store.commit('setPolicyExpired', false);
      store.dispatch('checkExpiration');
      return;
    }

    const offerId = offer.id;

    store.commit('setOfferLoading', { offerId, loading: true });

    const hotel = store.rootState.hotel.hotel;

    const policyOffer = await HotelService.policyMl(hotel.id, offerId)
      .toPromise()
      .catch(err => {
        store.dispatch('onSoldOut', offer);
      });

    if (!policyOffer) {
      return;
    }

    store.dispatch(
      'support/setRefreshDialogShow',
      { show: false, message: null },
      { root: true },
    );

    const {
      price,
      priceWithCredits,
      cancellationPolicies,
      paymentSchedule,
      orderFlow,
      billingCurrency,
      expirationTime,
    } = policyOffer;

    // merge offer with policy

    const offerWithPolicy = {
      ...offer,
      price,
      priceWithCredits,
      cancellationPolicies,
      paymentSchedule,
      orderFlow,
      billingCurrency,
    };

    const expirationDatetime = new Date(expirationTime);
    const expirationAt = expirationDatetime.isValid()
      ? expirationDatetime
      : null;

    store.commit('setPolicyExpirationTime', expirationAt);
    ExpirationCountdown.init(expirationAt, () =>
      store.commit('setPolicyExpired', true),
    );

    store.commit('setSelectedOffer', offerWithPolicy);

    if (
      offerWithPolicy.orderFlow ===
      HotelOffers.OrderFlow.expediaPayAtProperty2fa
    ) {
      await Expedia.fetchDependecy();
      await Expedia.initConnector();
    }

    // Events.Hotel.Offer.selectOffer({
    //   hotel,
    //   offer,
    // });

    store.dispatch('support/updateIntercomSettings', {}, { root: true });

    store.commit('setOfferLoading', { offerId, loading: false });
  },

  onSoldOut(store: ActionContext<State, any>, offer: HotelOffers.Offer) {
    store.commit('setOfferSoldout', {
      priceToken: offer.price._token,
      soldout: true,
    });
    if (store.getters.isOffersSoldOut) {
      const CURRENCY = store.rootState.support
        .currency as Currency.CurrencyItem;
      HotelService.getHotelv2(
        store.rootState.searchCriteria.criteria,
        CURRENCY.currency,
      ).subscribe(
        res => {
          if (res.datetime != store.state.request_id) {
            store.dispatch(
              'support/setRefreshDialogShow',
              { show: true, message: null },
              { root: true },
            );
          }
        },
        err => {
          store.dispatch(
            'support/setRefreshDialogShow',
            { show: false, message: null },
            { root: true },
          );
        },
      );
    }
  },

  setOfferSoldout(
    store: ActionContext<State, any>,
    { priceToken, soldout }: { priceToken: string; soldout: boolean },
  ) {
    store.commit('setOfferSoldout', { priceToken, soldout: soldout });
  },

  setError(store: ActionContext<State, any>, error: FPError) {
    if (error) {
      store.commit('setError', error);
    }
  },

  onDestroy(store: ActionContext<State, any>) {
    store.commit('resetStore');
    store.dispatch('booking/onDestroy', {}, { root: true });
    store.dispatch('card/onDestroy', {}, { root: true });
  },

  updateOffers(store: ActionContext<State, any>) {
    store.dispatch('selectOffer', { offer: null });
    store.dispatch('getOffers', true);
  },

  toggleIsOffersTrimmed(
    store: ActionContext<State, any>,
    isTrimmed: State['isOffersTrimmed'],
  ) {
    store.commit('setIsOffersTrimmed', isTrimmed);
    HotelOffers.OffersTrim.store(isTrimmed);
  },

  checkExpiration(store: ActionContext<State, any>) {
    if (store.state.selectedOffer) {
      if (!store.state.policyExpirationAt) {
        store.commit('setPolicyExpired', false);
        return;
      }

      ExpirationCountdown.resume(store.state.policyExpirationAt, () =>
        store.commit('setPolicyExpired', true),
      );
      return;
    }

    if (!store.state.offersExpirationAt) {
      store.commit('setOffersExpired', false);
      return;
    }
    ExpirationCountdown.resume(store.state.offersExpirationAt, () =>
      store.commit('setOffersExpired', true),
    );
  },

  async getLocalizedPolicy(store: ActionContext<State, any>) {
    if (
      store.state.localizedPolicy?.descriptions &&
      store.state.localizedPolicy?.policy
    ) {
      return;
    }

    const hotel = store.state.hotel;
    const lang = store.rootState.support.lang;
    if (lang.locale === Lang.LANGS.UK && hotel) {
      store.commit('setLocalizedPolicy', {
        isLoading: false,
        policy: hotel?.policy,
        descriptions: hotel?.descriptions,
      });
      return;
    }

    try {
      const criteria = store.rootState.searchCriteria.criteria;
      store.commit('setLocalizedPolicyLoading', true);
      const { policy, descriptions } = await HotelService.searchV2GetHotel(
        criteria?.id,
        true,
      ).toPromise();
      store.commit('setLocalizedPolicy', {
        isLoading: false,
        policy: policy,
        descriptions: descriptions,
      });
    } catch (error) {
      console.error(error);
    } finally {
      store.commit('setLocalizedPolicyLoading', false);
    }
  },

  onCarouselImageLoadError(store: ActionContext<State, any>, index: number) {
    if (!store.state.carousel.isOpen || !store.state.carousel.images[index]) {
      return Promise.reject('no image');
    }

    const image = store.state.carousel.images[index];
    const url = new URL(image);
    if (store.state.carousel.id === 'hotel-main-photos') {
      if (url.search.length === 0) {
        Log.onImageError(image, window.location.href);
        return Promise.resolve(image);
      }
      const src = url.origin + url.pathname;

      store.commit('updateHotelMediaValue', {
        index: index,
        value: src,
      });
      return Promise.resolve(src);
    } else {
      const params = url.searchParams;
      params.set('time', String(Date.now()));

      store.commit('updateImage', {
        index: index,
        value: url.toString(),
      });
      return Promise.resolve(url.toString());
    }
  },

  clearExpirationCountdown(store: ActionContext<State, any>) {
    ExpirationCountdown.stop();
    store.commit('clearExpirations');
  },
};

export const hotel = {
  namespaced: true,
  state: new State(),
  getters,
  mutations,
  actions,
};

export default {};
